import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Team } from '@/types/team';

export const useUserTeams = (clubId?: string) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUserTeams = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get the current user's effective profile ID (handles impersonation)
      const { data: profileResult, error: profileError } = await supabase
        .rpc('get_effective_profile_id');
      
      if (profileError) {
        console.error('Error getting effective profile ID:', profileError);
        throw profileError;
      }
      
      if (!profileResult) {
        setTeams([]);
        return;
      }
      
      // First get team IDs where user is a player
      const { data: playerTeams, error: playerError } = await supabase
        .from('players')
        .select('team_id')
        .eq('profile_id', profileResult);
      
      if (playerError) {
        console.error('Error fetching player teams:', playerError);
        throw playerError;
      }
      
      // Extract team IDs
      const teamIds = playerTeams?.map(p => p.team_id).filter(Boolean) || [];
      
      if (teamIds.length === 0) {
        setTeams([]);
        return;
      }
      
      // Build query to get team stats for user's teams
      let query = supabase
        .from('team_stats')
        .select('*')
        .in('id', teamIds);

      // Filter by club if provided
      if (clubId) {
        query = query.eq('club_id', clubId);
      }

      const { data, error } = await query.order('name');
      if (error) throw error;

      // Transform the data to match the Team interface
      const transformedData = data?.map(team => ({
        id: team.id,
        name: team.name,
        club_id: team.club_id,
        season_id: team.season_id,
        division_id: team.division_id,
        birth_date_cutoff: team.birth_date_cutoff,
        created_at: team.created_at,
        profile_picture_url: team.profile_picture_url,
        player_count: team.player_count,
        clubs: team.club_name ? { 
          id: team.club_id, 
          name: team.club_name,
          organizations: null as any
        } : null,
        seasons: team.season_name ? {
          id: team.season_id,
          name: team.season_name,
          season_year: team.season_year,
          status: team.season_status
        } : null,
        divisions: team.division_name ? { 
          id: team.division_id, 
          name: team.division_name 
        } : null,
        team_coaches: [] as any[]
      })) || [];
      
      setTeams(transformedData);
    } catch (error) {
      console.error('Error fetching user teams:', error);
      toast.error('Failed to load your teams');
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    fetchUserTeams();
  }, [fetchUserTeams]);

  return {
    teams,
    loading,
    fetchTeams: fetchUserTeams
  };
};